import { Badge } from '@reflex/ui/components/ui/badge';
import { cn } from '@reflex/ui/lib/utils';
import { SANDBOX_RESOURCE_SIZE_OPTIONS } from '@reflex/shared';
import { badgeClass } from '@/lib/status-colors';
import type { ResourceSize } from '@/lib/types';

type SemanticColor = keyof typeof badgeClass;

/**
 * Web-only display metadata per devbox size: the short label shown on the
 * badge and the semantic accent color that tints it. The tier (how many meter
 * bars light up) and the full human label + vCPU/RAM spec come from
 * {@link SANDBOX_RESOURCE_SIZE_OPTIONS} so this map only carries the bits that
 * are purely about how the chip looks. The colors form a deliberate cool to
 * warm ramp so a larger box reads as "hotter" at a glance.
 */
const SIZE_DISPLAY: Record<ResourceSize, { short: string; color: SemanticColor }> = {
  SMALL: { short: 'S', color: 'teal' },
  MEDIUM: { short: 'M', color: 'sky' },
  LARGE: { short: 'L', color: 'indigo' },
  X_LARGE: { short: 'XL', color: 'violet' },
  XX_LARGE: { short: '2XL', color: 'orange' },
};

/**
 * Fixed bar heights for the size meter, shortest to tallest. The array length
 * is the total number of bars; the size's tier decides how many light up.
 */
const BAR_HEIGHTS = ['h-[3px]', 'h-[5px]', 'h-[7px]', 'h-[9px]', 'h-[11px]'] as const;

export interface DevboxSizeBadgeProps {
  /** Devbox resource size (`agent.sandboxOptions.resourceSize`). */
  size: ResourceSize;
  className?: string;
  'data-testid'?: string;
}

/**
 * Compact badge for an agent's devbox resource size. Renders a small
 * signal-strength style meter whose lit bar count tracks the size tier
 * (SMALL = 1 bar, XX-Large = all bars) alongside a short label, both tinted by
 * a per-size accent color. The full label and vCPU/RAM spec surface on hover
 * via the native `title`. Sits beside the sandbox state badge in
 * {@link import('./SandboxPreview').SandboxPreview} so a glance tells you both
 * how the box is doing and how big it is.
 */
export function DevboxSizeBadge({
  size,
  className,
  'data-testid': dataTestId,
}: DevboxSizeBadgeProps) {
  const display = SIZE_DISPLAY[size];
  // The option order is the source of truth for the tier and the spec text.
  const optionIndex = SANDBOX_RESOURCE_SIZE_OPTIONS.findIndex((option) => option.value === size);
  const option = optionIndex >= 0 ? SANDBOX_RESOURCE_SIZE_OPTIONS[optionIndex] : null;
  // 1-indexed tier → number of lit meter bars.
  const litBars = optionIndex >= 0 ? optionIndex + 1 : 1;
  const label = option?.label ?? size;
  const title = option ? `${label} · ${option.description}` : label;

  return (
    <Badge
      variant="outline"
      data-testid={dataTestId ?? 'devbox-size-badge'}
      data-size={size}
      data-tier={litBars}
      title={title}
      aria-label={`Devbox size: ${title}`}
      className={cn(
        'gap-1 px-1.5 py-0 text-[10px] font-medium',
        badgeClass[display.color],
        className,
      )}
    >
      <span className="flex items-end gap-[2px]" aria-hidden="true">
        {BAR_HEIGHTS.map((height, index) => (
          <span
            key={height}
            className={cn(
              'w-[3px] rounded-[1px]',
              height,
              index < litBars ? 'bg-current' : 'bg-current/25',
            )}
          />
        ))}
      </span>
      <span className="tabular-nums">{display.short}</span>
    </Badge>
  );
}
